// eslint-disable-next-line @shopify/no-fully-static-classes -- scripts/audit-theme-snippets.ts locates this export by the literal "class ThemeEvents"
export class ThemeEvents {
  static variantSelected = 'variant:selected'
  static variantUpdate = 'variant:update'
  static cartUpdate = 'cart:update'
  static cartError = 'cart:error'
  static quantitySelectorUpdate = 'quantity-selector:update'
  static discountUpdate = 'discount:update'
  static scrollLockChanged = 'scroll:lock'
  static mediaStartedPlaying = 'media:started-playing'
}

class ScrollLockEvent extends Event {
  /** @param {boolean} isLocked */
  constructor(isLocked) {
    super(ThemeEvents.scrollLockChanged, { bubbles: true })
    this.detail = {
      isLocked
    }
  }
}

/**
 * @param {boolean} isLocked
 * @returns {void}
 */
export function scrollLock(isLocked) {
  document.dispatchEvent(new ScrollLockEvent(isLocked))
}

const SCROLL_LOCK_OWNERS = [
  'dialog[open]',
  '.mega-menu[data-open]',
  '.product-story__pip--visible',
  '[data-modal-whatsapp-status="active"]',
  '[data-scroll-lock-owner]'
].join(', ')

/** @returns {boolean} */
export function hasScrollLockOwner() {
  const owners = document.querySelectorAll(SCROLL_LOCK_OWNERS)
  return [...owners].some((owner) => !owner.closest('[lock-scroll="false"]'))
}

export function releaseScrollLock() {
  if (!hasScrollLockOwner()) scrollLock(false)
}

document.addEventListener(ThemeEvents.scrollLockChanged, ({ detail }) => {
  document.documentElement.classList.toggle('nav-is-open', detail.isLocked)
})

export class VariantSelectedEvent extends Event {
  /** @param {{ id: string }} resource */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true })
    this.detail = {
      resource
    }
  }
}

export class VariantUpdateEvent extends Event {
  /**
   * @param {object | null} resource
   * @param {string} sourceId
   * @param {{ html: Document | Element, newProduct: object | null, productId: string }} data
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true })
    this.detail = {
      data: {
        html: data.html,
        newProduct: data.newProduct,
        productId: data.productId
      },
      resource: resource || null,
      sourceId
    }
  }
}

export class CartAddEvent extends Event {
  static eventName = ThemeEvents.cartUpdate
  /**
   * @param {object} resource
   * @param {string} sourceId
   * @param {Record<string, unknown>} data
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true })
    this.detail = {
      data: {
        ...data
      },
      resource,
      sourceId
    }
  }
}

export class CartUpdateEvent extends Event {
  /**
   * @param {object} resource
   * @param {string} sourceId
   * @param {Record<string, unknown>} data
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true })
    this.detail = {
      data: {
        ...data
      },
      resource,
      sourceId
    }
  }
}

export class CartErrorEvent extends Event {
  /**
   * @param {string} sourceId
   * @param {string} message
   * @param {string} [description]
   * @param {Record<string, string[]> | undefined} [errors]
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true })
    this.detail = {
      data: {
        description,
        errors,
        message
      },
      sourceId
    }
  }
}

export class QuantitySelectorUpdateEvent extends Event {
  /**
   * @param {number} quantity
   * @param {number} cartLine
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true })
    this.detail = {
      cartLine,
      quantity
    }
  }
}

export class DiscountUpdateEvent extends Event {
  /**
   * @param {object} resource
   * @param {string} sourceId
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true })
    this.detail = {
      resource,
      sourceId
    }
  }
}

export class MediaStartedPlayingEvent extends Event {
  /** @param {Element} resource */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true })
    this.detail = {
      resource
    }
  }
}
